import { Product } from '@components/ui/Product';
import { LineBatchMeta } from '@shared/components/ui/LineBatchMeta';
import type { BatchSummary } from '@shared/models/BatchSummary';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { useToast } from '@shared/hooks/use-toast';
import { faCheck, faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Location } from '../../models/PreparationModels';
import { PreparationLineWithProduct } from '../../hooks';
import { resolvePickingBatchAtLocation } from '../../utils/resolvePickingBatchAtLocation';
import { hasRenderableLineBatchMeta } from '@shared/utils/sanitizeBatchDisplay';
import { ScannerInput } from './ScannerInput';

interface QuantityConfirmProps {
  selectedLine: PreparationLineWithProduct;
  selectedLocationCode: string;
  /** Ubicación ya escaneada: define el lote cuando el API lo enlaza a la preparación */
  selectedLocation?: Location | null;
  onConfirm: (
    quantity: number,
    batchId?: number,
  ) =>
    | { success: boolean; message: string; quantity?: number }
    | Promise<{ success: boolean; message: string; quantity?: number }>;
  onCancel: () => void;
}

export const QuantityConfirm: React.FC<QuantityConfirmProps> = ({
  selectedLine,
  selectedLocationCode,
  selectedLocation,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation('pickingModule');
  const { toast } = useToast();
  const maxQuantity = selectedLine.qty - selectedLine.qty_done;
  const [quantity, setQuantity] = useState(Math.min(maxQuantity, 1));
  const [isConfirming, setIsConfirming] = useState(false);

  const resolvedBatch = useMemo(
    () => resolvePickingBatchAtLocation(selectedLine, selectedLocation ?? undefined),
    [selectedLine, selectedLocation],
  );

  const batchSummary: BatchSummary | undefined = useMemo(() => {
    if (!resolvedBatch) return undefined;
    return {
      id: resolvedBatch.batchId,
      batch_number: resolvedBatch.batch?.batch_number ?? undefined,
      expiry_date: resolvedBatch.batch?.expiry_date ?? undefined,
    };
  }, [resolvedBatch]);

  const handleScan = (scannedCode: string) => {
    const trimmedScannedCode = scannedCode.trim();
    const expectedCode = selectedLine.product?.code?.trim() || selectedLine.product?.ean?.trim() || '';

    if (trimmedScannedCode === expectedCode) {
      if (quantity < maxQuantity) {
        const newQuantity = quantity + 1;
        setQuantity(newQuantity);
        return {
          success: true,
          message: `Cantidad incrementada a ${newQuantity}`,
        };
      }
      return {
        success: false,
        message: `Cantidad máxima alcanzada (${maxQuantity})`,
      };
    }
    return {
      success: false,
      message: 'Código incorrecto. Escanea el producto seleccionado',
    };
  };

  const handleConfirm = async () => {
    if (quantity <= 0 || quantity > maxQuantity) return;

    setIsConfirming(true);
    try {
      const result = await onConfirm(quantity, resolvedBatch?.batchId);

      if (!result.success) {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('❌ QuantityConfirm - Error al confirmar picking:', error);
      toast({
        title: 'Error',
        description: 'Error al confirmar el picking',
        variant: 'destructive',
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(Math.max(1, Math.min(newQuantity, maxQuantity)));
  };

  return (
    <div className="bg-background border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Confirmar picking</h3>
        <div className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 px-2 py-1 rounded text-sm font-medium">
          {selectedLocationCode}
        </div>
      </div>

      <div className="mb-4">
        <ScannerInput
          currentStep="product"
          stepDescription={`Escanear para incrementar cantidad (Producto esperado: ${selectedLine.product?.code || selectedLine.product?.ean || 'N/A'})`}
          onScan={handleScan}
        />
      </div>

      <div className="mb-4">
        <Product
          id={selectedLine.product_id}
          src={selectedLine.product?.image || ''}
          name={selectedLine.product?.name || `Producto ID: ${selectedLine.product_id}`}
          code={selectedLine.product?.code}
          acronym={selectedLine.product?.name?.charAt(0).toUpperCase()}
          showImageModal={true}
          enableLineClamp={true}
        />
      </div>

      {resolvedBatch &&
      batchSummary &&
      hasRenderableLineBatchMeta({
        batch: batchSummary,
        batchId: resolvedBatch.batchId,
        hideNumericIdFallback: true,
      }) ? (
        <div className="mb-4">
          <p className="text-xs font-medium text-muted-foreground mb-2">{t('quantity_confirm.batch_heading')}</p>
          <LineBatchMeta
            batch={batchSummary}
            batchId={resolvedBatch.batchId}
            hideNumericIdFallback
            i18nNs="pickingModule"
            lotLabelKey="quantity_confirm.batch_label"
            expiryLabelKey="quantity_confirm.expiry_label"
          />
        </div>
      ) : null}

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">Cantidad a recoger</span>
          <span className="text-sm text-muted-foreground">Pendiente: {maxQuantity}</span>
        </div>

        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <StepperInput
              value={quantity}
              onChange={handleQuantityChange}
              min={1}
              max={maxQuantity}
              step={1}
              allowNegative={false}
              data-testid="picking-quantity-stepper"
            />
          </div>

          <button
            type="button"
            onClick={() => handleQuantityChange(maxQuantity)}
            disabled={quantity >= maxQuantity}
            className="px-2 py-1 text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="picking-quantity-max-button"
          >
            Máx
          </button>
        </div>
      </div>

      <div className="flex space-x-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isConfirming}
          className="flex-1 py-2 px-3 border border-border rounded font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          <FontAwesomeIcon icon={faTimes} className="w-4 h-4 mr-2" />
          Cancelar
        </button>

        <button
          type="button"
          onClick={handleConfirm}
          disabled={isConfirming || quantity <= 0 || quantity > maxQuantity}
          className="flex-1 py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded font-medium focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {isConfirming ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Confirmando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheck} className="w-4 h-4 mr-2" />
              Confirmar
            </>
          )}
        </button>
      </div>
    </div>
  );
};
